import type { FC } from 'react';
import React, { useContext, useEffect, useRef } from 'react';
import { configure, GlobalHotKeys } from 'react-hotkeys';

import { EditorContext, ModalServiceContext, RicosContext, ShortcutsContext } from './../ricos-context';
import type { ModalService } from './../ricos-modals';
import type { KeyboardShortcut } from './../ricos-types';

import { blocksMenuShortcuts } from './blocks-menu-shortcuts';
import type { Shortcuts as ShortcutsManager } from './editor-keyboard-shortcuts';
import { generalShortcuts } from './general-shortcuts';
import { keyByKeyCode } from './key-codes';
import { navigationShortcuts } from './navigation-shortcuts';
import { ShortcutsDialog } from './ShortcutsDialog';

export type ShortcutsProps = {
  group: string;
  root?: boolean;
};

const defaultProps = {
  group: 'global',
  root: false,
  children: null,
  plugins: [],
};

// TODO: move to utils
const useComponentWillMount = (callback: () => void) => {
  const mounted = useRef(false);
  if (!mounted.current) callback();

  useEffect(() => {
    mounted.current = true;
  }, []);
};

export const Shortcuts: FC<ShortcutsProps> = (props: ShortcutsProps) => {
  const { group, root } = { ...defaultProps, ...props };

  const modalService: ModalService = useContext(ModalServiceContext);

  const shortcuts = useContext(ShortcutsContext) as ShortcutsManager;
  const helpShortcut: KeyboardShortcut = {
    name: 'OpenShortcutsSheet',
    description: 'ShortcutsSheet_General_OpenKeyboardShortcutsSheet',
    group: 'ShortcutsSheet_Section_General',
    command() {
      modalService.openModal({
        id: 'shortcuts-help',
        Component: ShortcutsDialog,
        positioning: { placement: 'right' },
        componentProps: {},
        layout: 'drawer',
      });
    },
    keys: {
      macOs: 'Meta+/',
      windows: 'Ctrl+/',
    },
    enabled: true,
  };
  if (root && shortcuts.asArray().filter(s => s.getName() === 'OpenShortcutsSheet').length === 0) {
    shortcuts.register(helpShortcut);
    generalShortcuts.forEach(s => shortcuts.register(s));
    navigationShortcuts.forEach(s => shortcuts.register(s));
    blocksMenuShortcuts.forEach(s => shortcuts.register(s));
  }

  const { t, platform } = useContext(RicosContext);
  const { getPublicCommands, adapter } = useContext(EditorContext);
  const publicCommands = getPublicCommands();
  const { handlers, keyMap } = shortcuts.getHotKeysProps({
    group,
    publicCommands,
    editor: adapter.tiptapEditor,
    t,
    platform,
  });

  useComponentWillMount(() => {
    if (root) {
      configure({
        ignoreTags: [],
        ignoreEventsCondition: () => false,
        customKeyCodes: keyByKeyCode,
      });
    }
  });

  return <GlobalHotKeys handlers={handlers} keyMap={keyMap} />;
};
